import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "src/redux/store";

export interface GlobalState {
  username: string;
  name: string;
  createdAt: string;
  avatar: string;
  userId: string;
  plan: string;
  period: string; // Monthly or Yearly
  trialEnd: string;
  showMessagePopup: boolean;
  userToken: string;
  userInviteToken: string;
  listingId: string;
  isFirstTime: boolean;
  isFirstTimeListingsReady: boolean;
  pms: string;
  perUserPrompts: string;
  neverEscalateDraftMode: boolean;
  offerLanguage: string;
  summaryLanguage: string;
  autopilotSignature: string | null; // will be the sign off of the user
  autoEnableListings: boolean;
  extensionInstalledAt: string | null;
  hostCompanyName: string | null;
  hostAgentName: string | null;
  webAppCredsIsExpired: boolean | null;
  templateFirstName: string;
  numListings: number;
  widgetEligible: boolean;
  widgetEnabled: boolean;
  churnkeyHash: string | null;
  stripeCustomerId: string | null;
  listingSetsEnabled: boolean;
  connectedStripeAccountInfo: object | null;
  stripeIntentSecret: string | null;
  savedPaymentMethod: boolean | null;
  numListingsPaidFor: number;
  bffForwardCallsTo: string | null;
  offersOnlyMode: boolean;
  isHomiePrimary: boolean;
  isHomieEnabled: boolean;
  subUserEmail: string | null;
  emailNotificationsEnabled: boolean;
  unreadCount: number;
}

const initialState: GlobalState = {
  username: "",
  name: "",
  createdAt: "",
  avatar: "",
  userId: "",
  plan: "",
  period: "",
  trialEnd: "",
  showMessagePopup: true,
  userToken: "",
  userInviteToken: "",
  listingId: "",
  isFirstTime: false,
  isFirstTimeListingsReady: false,
  pms: "",
  perUserPrompts: "",
  neverEscalateDraftMode: true,
  offerLanguage: "en",
  summaryLanguage: "en",
  autopilotSignature: null,
  autoEnableListings: true,
  extensionInstalledAt: null,
  hostCompanyName: null,
  hostAgentName: null,
  webAppCredsIsExpired: false,
  templateFirstName: "",
  numListings: 0,
  widgetEligible: false,
  widgetEnabled: false,
  churnkeyHash: null,
  stripeCustomerId: null,
  listingSetsEnabled: false,
  connectedStripeAccountInfo: null,
  stripeIntentSecret: null,
  savedPaymentMethod: false,
  numListingsPaidFor: 0,
  bffForwardCallsTo: null,
  offersOnlyMode: false,
  isHomiePrimary: false,
  isHomieEnabled: false,
  subUserEmail: null,
  emailNotificationsEnabled: false,
  unreadCount: 0,
};

const globalSlice = createSlice({
  name: "global",
  initialState,
  reducers: {
    setUserInfo: (state, action) => {
      state.username = action.payload.username;
      state.name = action.payload.name;
      state.createdAt = action.payload.createdAt;
      state.avatar = action.payload.avatar;
      state.userId = action.payload.userId;
      state.plan = action.payload.plan;
      state.period = action.payload.period;
      state.trialEnd = action.payload.trialEnd;
      state.showMessagePopup = action.payload.showMessagePopup;
      state.userToken = action.payload.userToken;
      state.userInviteToken = action.payload.userInviteToken;
      state.listingId = action.payload.listingId;
      state.isFirstTime = action.payload.isFirstTime;
      state.pms = action.payload.pms;
      state.isFirstTimeListingsReady = action.payload.isFirstTimeListingsReady;
      state.perUserPrompts = action.payload.perUserPrompts;
      state.neverEscalateDraftMode = action.payload.neverEscalateDraftMode;
      state.offerLanguage = action.payload.offerLanguage;
      state.summaryLanguage = action.payload.summaryLanguage;
      state.autopilotSignature = action.payload.autopilotSignature;
      state.autoEnableListings = action.payload.autoEnableListings;
      state.extensionInstalledAt = action.payload.extensionInstalledAt;
      state.hostCompanyName = action.payload.hostCompanyName;
      state.hostAgentName = action.payload.hostAgentName;
      state.webAppCredsIsExpired = action.payload.webAppCredsIsExpired;
      state.templateFirstName = action.payload.templateFirstName;
      state.numListings = action.payload.numListings;
      state.widgetEligible = action.payload.widgetEligible;
      state.widgetEnabled = action.payload.widgetEnabled;
      state.churnkeyHash = action.payload.churnkeyHash;
      state.stripeCustomerId = action.payload.stripeCustomerId;
      state.listingSetsEnabled = action.payload.listingSetsEnabled;
      state.connectedStripeAccountInfo = action.payload.connectedStripeAccountInfo;
      state.stripeIntentSecret = action.payload.stripeIntentSecret;
      state.savedPaymentMethod = action.payload.savedPaymentMethod;
      state.numListingsPaidFor = action.payload.numListingsPaidFor;
      state.bffForwardCallsTo = action.payload.bffForwardCallsTo;
      state.offersOnlyMode = action.payload.offersOnlyMode;
      state.isHomiePrimary = action.payload.isHomiePrimary;
      state.isHomieEnabled = action.payload.isHomieEnabled;
      state.subUserEmail = action.payload.subUserEmail;
      state.emailNotificationsEnabled = action.payload.emailNotificationsEnabled;
    },
    setUserToken: (state, action) => {
      state.userToken = action.payload;
    },
    setUnreadCount: (state, action: PayloadAction<number>) => {
      state.unreadCount = action.payload;
    },
  },
});

export const { setUserInfo, setUserToken, setUnreadCount } = globalSlice.actions;

export const getUserInfo = (state: RootState) => {
  // console.log('getUserInfo state:', state.global);
  return state.global;
};

export const getUnreadCount = (state: RootState): { unreadCount: number } => {
  // Assuming 'unansweredMessages' exists on state.messages and is a number
  return { unreadCount: state.global.unreadCount };
};

export default globalSlice.reducer;
